import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Badge,
  Label,
  Button,
  Input,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import { MULTI_CORRECT_NEGATIVE_OPTIONS } from "components/constant";

const initialFields = {
  questionBankName: { value: "Electrical Safety – Level 1", error: "" },
  level: { value: "Advanced", error: "" },
  tag: { value: "Electrical", error: "" },
  status: { value: "ACTIVE", error: "" },
  totalQuestions: { value: 4, error: "" },
  requiredQuestions: { value: 2, error: "" },
  questionTypes: {
    value: ["Single Choice", "Multiple Choice", "True / False", "Short Answer"],
    error: "",
  },
  description: {
    value:
      "This question bank evaluates core electrical safety knowledge required for technicians.",
    error: "",
  },
};

const questionsList = [
  {
    id: 1,
    question: "What is the safe voltage limit for domestic wiring?",
    type: "MCQ",
    required: true,
    options: [
      { text: "110V", grade: 0 },
      { text: "220V", grade: 1 },
      { text: "440V", grade: -0.25 },
      { text: "660V", grade: -0.5 },
    ],
    answer: [1],
  },
  {
    id: 2,
    question: "Select all safety equipment used in electrical work",
    type: "MULTI",
    required: true,
    options: [
      { text: "Insulated gloves", grade: 0.333333 },
      { text: "Safety helmet", grade: 0.333333 },
      { text: "Rubber mat", grade: 0.333333 },
      { text: "Plastic chair", grade: -0.5 },
    ],
    answer: [0, 1, 2],
  },
  {
    id: 3,
    question: "Earthing is mandatory for all electrical installations",
    type: "TF",
    required: false,
    options: [
      { text: "True", grade: 1 },
      { text: "False", grade: -1 },
    ],
    answer: [0], //
  },
];

function QuestionBankDetails() {
  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;
  const [data] = useState(initialFields);
  const navigate = useNavigate();

  useEffect(() => {
    if (decodedId) {
      // API call later
    }
  }, [decodedId]);

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        {/* HEADER */}
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <i
                    className="fa fa-chevron-left mr-2 cursor"
                    onClick={() => navigate("/admin/question-bank")}
                  />

                  <div>
                    <h3 className="mb-0">{data.questionBankName.value}</h3>
                    <small className="text-muted">
                      Level: {data.level.value} | Tag:{" "}
                      {data.tag.value}
                    </small>
                  </div>
                </div>

                <Button
                  className="btn-deep-blue"
                >
                  <i className="fa fa-pencil mr-1" />
                  Edit
                </Button>
              </CardHeader>
            </Card>
          </Col>
        </Row>

        {/* QUESTIONS LIST */}
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <h4 className="mb-0">
                  Total Questions: {questionsList.length}
                </h4>
              </CardHeader>

              <CardBody>
                {questionsList.map((q, index) => (
                  <Card key={q.id} className="mb-3">
                    <CardBody>
                      {/* HEADER */}
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <strong className="d-block">
                            Q{index + 1}. {q.question}
                          </strong>
                        </div>

                        <div className="d-flex align-items-center">
                          <Badge color="secondary" pill>
                            {q.type}
                          </Badge>
                        </div>
                      </div>

                      {/* BODY */}
                      {(q.type === "MCQ" || q.type === "MULTI") && (
                        <div className="mt-2 ml-4">
                          {q.options.map((opt, i) => {
                            const isCorrect = q.answer?.includes(i);

                            return (
                              <div
                                key={i}
                                className="d-flex align-items-center mb-1"
                              >
                                <Input
                                  type={q.type === "MCQ" ? "radio" : "checkbox"}
                                  disabled
                                  checked={isCorrect}
                                  className="mr-2"
                                />

                                <span
                                  className={
                                    isCorrect
                                      ? "text-success font-weight-bold"
                                      : "text-muted"
                                  }
                                >
                                  {opt.text}

                                  <span className="ml-2 text-muted small">
                                    (
                                    {
                                      MULTI_CORRECT_NEGATIVE_OPTIONS.find(
                                        (g) => g.value === opt.grade,
                                      )?.label
                                    }
                                    )
                                  </span>
                                </span>

                                {isCorrect && (
                                  <i className="fa fa-check-circle text-success ml-2" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {q.type === "TF" && (
                        <div className="mt-2 ml-1">
                          {q.options.map((opt, i) => (
                            <div
                              key={i}
                              className="d-flex align-items-center mb-1"
                            >
                              <span
                                className={
                                  q.answer?.[0] === i
                                    ? "text-success font-weight-bold"
                                    : "text-muted"
                                }
                              >
                                {opt.text}

                                <span className="ml-2 text-muted small">
                                  (
                                  {
                                    MULTI_CORRECT_NEGATIVE_OPTIONS.find(
                                      (g) => g.value === opt.grade,
                                    )?.label
                                  }
                                  )
                                </span>
                              </span>

                              {q.answer?.[0] === i && (
                                <i className="fa fa-check-circle text-success ml-2" />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default QuestionBankDetails;
